import { getIntersectionRoot, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';
import { setupCapsuleVideo } from '@theme/olsun-capsule-video';

// Silent in-view video loops; pause remains available by tap or keyboard without
// a player toolbar. Ingredient and nutrition content is server-rendered.
if (!customElements.get('olsun-capsule-details')) {
  customElements.define('olsun-capsule-details', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      this.setupIngredients();
      // A new page visit (including back/forward restoration) starts compact.
      // Ordinary scrolling and ingredient changes must not close user-opened details.
      const closeNutrition = () => this.querySelectorAll('details.ocd__nutrition').forEach(details => { details.open = false; });
      closeNutrition();
      window.addEventListener('pageshow', closeNutrition, { signal: this.controller.signal });
      this.setupScrollCover();
      this.setupMedia();
      this.setupVideoLoop();
    }

    setupMedia() {
      const controls = this.querySelector('.ocd__controls');
      if (!controls) return;
      const panels = [...this.querySelectorAll('[data-panel]')];
      const buttons = [...controls.querySelectorAll('button')];
      const show = (name) => {
        panels.forEach((panel) => {
          const selected = panel.dataset.panel === name;
          if (!selected) panel.querySelectorAll('video').forEach((video) => video.pause());
          panel.hidden = !selected;
        });
        buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.show === name)));
      };
      // Prefer the uploaded video, without a visible Photo / Video toolbar.
      controls.hidden = true;
      this.showMedia = show;
      buttons.forEach((button, index) => {
        button.addEventListener('click', () => show(button.dataset.show));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
            : (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
          buttons[next].focus();
          show(buttons[next].dataset.show);
        });
      });
      // A static video poster still permits deliberate play with reduced motion/data saving.
      show('video');
    }

    setupVideoLoop() {
      this.videoCleanup = setupCapsuleVideo(this, { getIntersectionRoot, scrollContainerMediaQuery, getScrollEventTarget });
    }

    setupIngredients() {
      const content = this.querySelector('.ocd__ingredients');
      const toggle = this.querySelector('.ocd__ingredients-toggle');
      if (!content || !toggle) return;
      let items = [...content.querySelectorAll('[data-ingredient-item]')];
      // Also support Shopify rich-text lists without losing their live HTML.
      if (!items.length) items = [...content.querySelectorAll(':scope > ul > li, :scope > ol > li, :scope > .metafield-rich_text_field > ul > li, :scope > .metafield-rich_text_field > ol > li')];
      if (items.length <= 3) return;
      const label = toggle.querySelector('[data-toggle-label]');
      const icon = toggle.querySelector('.ocd__plus');
      const setExpanded = (expanded) => {
        items.forEach((item, index) => { item.hidden = !expanded && index >= 3; });
        toggle.setAttribute('aria-expanded', String(expanded));
        label.textContent = expanded ? toggle.dataset.collapseLabel : toggle.dataset.expandLabel;
        if (icon) icon.textContent = expanded ? '−' : '+';
      };
      toggle.hidden = false;
      setExpanded(false);
      toggle.addEventListener('click', () => {
        const expanding = toggle.getAttribute('aria-expanded') !== 'true';
        setExpanded(expanding);
        // A long table can shrink above the viewport. Keep its control reachable
        // after a deliberate collapse; never change scroll during ordinary reading.
        if (!expanding) requestAnimationFrame(() => {
          const rect = toggle.getBoundingClientRect();
          if (rect.top < 0 || rect.bottom > window.innerHeight) {
            toggle.scrollIntoView({ block: 'center', behavior: 'instant' });
          }
        });
      }, { signal: this.controller.signal });
      document.addEventListener('shopify:block:select', (event) => {
        if (content.contains(event.target)) setExpanded(true);
      }, { signal: this.controller.signal });
    }

    setupScrollCover() {
      const section = this.closest('.olsun-capsule-details-section');
      const previous = section?.previousElementSibling;
      const product = previous?.querySelector(':scope > .product-information.opp');
      // The product stays in front and scrolls normally; this panel is revealed
      // underneath it. No extra scroll track or interception of purchase controls.
      if (this.dataset.scrollOver !== 'true' || !product) return;
      this.coverSection = section;
      this.underlay = previous;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      const update = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          if (!this.isConnected) return;
          if (this.motion.matches) { this.resetCover(); return; }
          const viewport = getIntersectionRoot()?.clientHeight || window.innerHeight;
          const height = this.offsetHeight;
          const reveal = Math.min(viewport, product.offsetHeight, height);
          section.style.setProperty('--ocd-reveal-height', `${reveal}px`);
          section.style.setProperty('--ocd-panel-height', `${height}px`);
          previous.classList.add('ocd-foreground');
          section.classList.add('is-product-reveal');
        });
      };
      this.coverObserver = new ResizeObserver(update);
      this.coverObserver.observe(product);
      this.coverObserver.observe(this);
      const root = getIntersectionRoot();
      if (root) this.coverObserver.observe(root);
      window.addEventListener('resize', update, { signal: this.controller.signal });
      this.motion.addEventListener('change', update, { signal: this.controller.signal });
      scrollContainerMediaQuery.addEventListener('change', update, { signal: this.controller.signal });
      update();
    }

    resetCover() {
      this.underlay?.classList.remove('ocd-foreground');
      this.coverSection?.classList.remove('is-product-reveal');
      this.coverSection?.style.removeProperty('--ocd-reveal-height');
      this.coverSection?.style.removeProperty('--ocd-panel-height');
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.coverObserver?.disconnect();
      this.videoCleanup?.();
      cancelAnimationFrame(this.frame);
      this.resetCover();
      this.initialized = false;
    }
  });
}
